import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Button, Card, CardBody, CardHeader, Col, Row, Spinner } from 'reactstrap'
import { IEffect } from './index'
import { toast } from 'react-toastify'
import { getAssetUrl } from '../../../helpers/backend_helper'
import DeleteModal from 'Components/Common/DeleteModal'
import { useDebouncedCallback } from 'use-debounce'

interface IProps {
  fetching: boolean
  effectRecords: IEffect[]
  handleOpenDetails: (arg: string) => void
  setOpenEffectPopup: (arg: boolean) => void
  setEditMode: (arg: boolean) => void
  handleDeleteEffect: (arg: string) => Promise<string>
}

const EffectsTable: React.FC<IProps> = ({
  effectRecords,
  handleOpenDetails,
  setOpenEffectPopup,
  setEditMode,
  fetching,
  handleDeleteEffect
}) => {
  const [showModalDelete, setShowModalDelete] = useState<boolean>(false)
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined)
  const [query, setQuery] = useState('')
  const [filteredRecords, setFilteredRecords] = useState<IEffect[]>(effectRecords)

  useEffect(() => {
    setFilteredRecords(effectRecords)
  }, [effectRecords])

  const handleAddEffect = () => {
    setEditMode(false)
    setOpenEffectPopup(true)
  }

  const handleEditEffect = (id: string | undefined) => {
    if (id) {
      setEditMode(true)
      setOpenEffectPopup(true)
      handleOpenDetails(id)
    }
  }

  const handleDeleteRecord = async (id: string | undefined) => {
    if (id) {
      const message = await handleDeleteEffect(id)
      if (message === 'SUCCESSFULL') {
        toast.success('Filtre başarıyla silindi')
      }
    }
  }

  const handleShowModalDelete = (id: string | undefined) => {
    if (id) {
      setShowModalDelete(true)
      setSelectedId(id)
    }
  }

  const handleDelete = () => {
    handleDeleteRecord(selectedId)
    setShowModalDelete(false)
  }

  const debouncedSearch = useDebouncedCallback((searchTerm: string) => {
    if (!searchTerm.trim()) {
      setFilteredRecords(effectRecords)
    } else {
      const filtered = effectRecords.filter((eff) =>
        eff.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
      setFilteredRecords(filtered)
    }
  }, 300)

  const handleInputChange = (e: { target: { value: string } }) => {
    const value = e.target.value
    setQuery(value)
    debouncedSearch(value)
  }

  return (
    <React.Fragment>
      <Row>
        <Col xl={12}>
          <Card>
            <CardHeader className='align-items-center d-flex justify-content-between'>
              <div className='search-box d-inline-block col-8 d-flex align-items-center'>
                <input
                  id='search-bar-effect'
                  className='form-control search'
                  placeholder='Filtre ara...'
                  onChange={handleInputChange}
                  value={query}
                />
                <i className='bx bx-search-alt search-icon'></i>
              </div>
              <Button className='p-2' color='primary' onClick={handleAddEffect}>
                <div className='flex align-items-center'>
                  {fetching ? (
                    <Spinner size='sm'> Loading... </Spinner>
                  ) : (
                    <>
                      <i className='mdi mdi-plus'></i>
                      <span className='fw-semibold ms-1'>Yeni Filtre</span>
                    </>
                  )}
                </div>
              </Button>
            </CardHeader>
            <CardBody>
              <div className='table-responsive table-card'>
                <table className='table align-middle table-nowrap table-striped-columns mb-0'>
                  <thead className='table-light'>
                    <tr>
                      <th scope='col' style={{ width: '80px' }}>Önizleme</th>
                      <th scope='col'>İsim</th>
                      <th scope='col'>Durum</th>
                      <th scope='col'>Aktif Booth Sayısı</th>
                      <th scope='col' style={{ width: '150px' }}>İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRecords.map((effect, index) => (
                      <tr key={index}>
                        <td>
                          <img
                            src={getAssetUrl(effect.imageLink)}
                            alt={effect.name}
                            style={{ width: '60px', height: '40px', objectFit: 'cover', borderRadius: '4px' }}
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = 'https://placehold.co/60x40?text=No+Image'
                            }}
                          />
                        </td>
                        <td>
                          <Link to='#' className='fw-medium'>
                            {effect.name}
                          </Link>
                        </td>
                        <td>
                          <span className={`badge ${effect.isActive ? 'bg-success' : 'bg-secondary'}`}>
                            {effect.isActive ? 'Aktif' : 'Pasif'}
                          </span>
                        </td>
                        <td>
                          <span className='badge bg-info'>
                            {effect.isActiveFor?.length || 0} booth
                          </span>
                        </td>
                        <td>
                          <button
                            type='button'
                            className='btn btn-sm btn-light'
                            onClick={() => handleEditEffect(effect?._id)}
                          >
                            Düzenle
                          </button>
                          <button
                            type='button'
                            className='btn btn-sm btn-danger ms-2'
                            onClick={() => handleShowModalDelete(effect?._id)}
                          >
                            Sil
                          </button>
                        </td>
                      </tr>
                    ))}
                    {filteredRecords.length === 0 && !fetching && (
                      <tr>
                        <td colSpan={5} className='text-center py-4'>
                          Filtre bulunamadı
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
      <DeleteModal
        show={showModalDelete}
        onDeleteClick={handleDelete}
        onCloseClick={() => setShowModalDelete(false)}
      />
    </React.Fragment>
  )
}

export default EffectsTable
